// A popover under a button (the pickers) and a menu of actions, both closed with Esc or a click elsewhere, with
// the focus going back to the button.

import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "./Icon";

/** A floating panel next to `anchor`, kept inside the window. */
export function Popover({ anchor, onClose, children, label, className = "" }: {
  anchor: RefObject<HTMLElement | null>; onClose: () => void; children: ReactNode; label: string; className?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const box = anchor.current?.getBoundingClientRect();
      const own = panel.current?.getBoundingClientRect();
      if (!box || !own) return;
      const margin = 12;
      let left = box.left;
      let top = box.bottom + 6;
      if (left + own.width > innerWidth - margin) left = innerWidth - margin - own.width;
      if (top + own.height > innerHeight - margin) top = Math.max(margin, box.top - 6 - own.height);
      setPosition({ left: Math.max(margin, left), top });
    };
    place();
    addEventListener("resize", place);
    return () => removeEventListener("resize", place);
  }, [anchor]);

  useEffect(() => {
    const returnTo = anchor.current;
    const down = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panel.current?.contains(target) || anchor.current?.contains(target)) return;
      onClose();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("pointerdown", down, true);
    panel.current?.addEventListener("keydown", key);
    const own = panel.current;
    return () => {
      document.removeEventListener("pointerdown", down, true);
      own?.removeEventListener("keydown", key);
      if (returnTo && document.activeElement === document.body) returnTo.focus({ preventScroll: true });
    };
  }, [anchor, onClose]);

  return createPortal(
    <div ref={panel} className={`popover ${className}`} role="dialog" aria-label={label}
      style={position ? { left: position.left, top: position.top } : { left: -9999, top: 0 }}
      onClick={(event) => event.stopPropagation()}>
      {children}
    </div>,
    document.body,
  );
}

export interface MenuItem {
  label: string;
  run: () => void;
  icon?: IconName;
  destructive?: boolean;
  /** A letter or key shown at the right. */
  hint?: string;
  /** A setting that is on or off: a tick shows while it is on. */
  checked?: boolean;
}

/** A button that opens a menu of actions: ↑ ↓ to move, Return to choose. */
export function Menu({ label, items, icon, className = "", children }: { label: string; items: MenuItem[]; icon?: IconName; className?: string; children?: ReactNode }) {
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) list.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [open]);
  const move = (event: ReactKeyboardEvent) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const buttons = [...(list.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    buttons[(index + (event.key === "ArrowDown" ? 1 : buttons.length - 1)) % buttons.length]?.focus();
  };
  return (
    <>
      <button ref={button} className={className || "menu-button"} aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open}
        onClick={(event) => { event.stopPropagation(); setOpen((value) => !value); }}>
        {icon && <Icon name={icon} />}{children}
      </button>
      {open && (
        <Popover anchor={button} onClose={() => setOpen(false)} label={label} className="menu">
          <div ref={list} role="menu" onKeyDown={move}>
            {items.map((item) => (
              <button key={item.label} role={item.checked === undefined ? "menuitem" : "menuitemcheckbox"} aria-checked={item.checked}
                className={item.destructive ? "destructive" : ""}
                onClick={() => { setOpen(false); item.run(); }}>
                {item.icon && <Icon name={item.icon} />}<span>{item.label}</span>{item.hint && <span className="kbd">{item.hint}</span>}
                {item.checked && <Icon name="check" className="menu-tick" />}
              </button>
            ))}
          </div>
        </Popover>
      )}
    </>
  );
}

/**
 * A row's menu at the pointer, on a right-click (the Mac's row menu, MacTodoMenu.swift): ↑ ↓ to move, Return to
 * choose, Esc, a click elsewhere or a scroll close it.
 */
export function ContextMenu({ at, items, label, onClose }: { at: { x: number; y: number }; items: MenuItem[]; label: string; onClose: () => void }) {
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const own = panel.current?.getBoundingClientRect();
    if (!own) return;
    const margin = 12;
    const left = at.x + own.width > innerWidth - margin ? Math.max(margin, at.x - own.width) : at.x;
    const top = at.y + own.height > innerHeight - margin ? Math.max(margin, at.y - own.height) : at.y;
    setPosition({ left, top });
  }, [at]);
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
    const down = (event: PointerEvent) => { if (!panel.current?.contains(event.target as Node)) onClose(); };
    const away = () => onClose();
    document.addEventListener("pointerdown", down, true);
    addEventListener("resize", away);
    addEventListener("scroll", away, true);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      removeEventListener("resize", away);
      removeEventListener("scroll", away, true);
      if (returnTo && document.activeElement === document.body) returnTo.focus({ preventScroll: true });
    };
  }, [onClose]);
  const key = (event: ReactKeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    event.stopPropagation();
    const buttons = [...(panel.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    buttons[(index + (event.key === "ArrowDown" ? 1 : buttons.length - 1)) % buttons.length]?.focus();
  };
  return createPortal(
    <div ref={panel} className="popover menu context-menu" aria-label={label}
      style={position ? { left: position.left, top: position.top } : { left: -9999, top: 0 }}
      onClick={(event) => event.stopPropagation()} onContextMenu={(event) => event.preventDefault()}>
      <div role="menu" aria-label={label} onKeyDown={key}>
        {items.map((item) => (
          <button key={item.label} role="menuitem" className={item.destructive ? "destructive" : ""}
            onClick={() => { onClose(); item.run(); }}>
            {item.icon && <Icon name={item.icon} />}<span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/**
 * A list's view switch on its title's line (List | Day in Today, Today | All in Habits, List | Week in Scheduled): one
 * look for all of them, Todolix's own (the display decisions of 2026-10-29, point 10 B).
 */
export function ViewSwitch<Value extends string>({ label, value, options, onChange }: {
  label: string; value: Value; options: readonly { value: Value; label: string; title?: string }[]; onChange: (value: Value) => void;
}) {
  return (
    <div className="view-switch" role="tablist" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} role="tab" aria-selected={option.value === value} className={option.value === value ? "on" : ""}
          title={option.title} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}
